/* workspace-admin/AuditLog — M-04: workspace-scoped audit log with filters + pagination.
   Fetches from GET /workspaces/:wsId/audit (backend already exists).
   Filters: search (free-text), actor (email), action, date range.
   Pagination: 25 per page with prev/next. */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Btn, Pill } from '../shared/ui';
import { PageHead } from '../platform/_ui';
import { findWorkspace } from '../platform/platformStore';
import * as workspaceApi from '../api/workspace.api.js';

const SEV_TONE = { info: 'blue', warning: 'amber', critical: 'red', error: 'red', success: 'green' };
const PAGE_SIZE = 25;

const fmt = (iso) =>
  iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—';

/* ── Filter bar ──────────────────────────────────────────────────────── */
function FilterRow({ filters, onChange, onClear }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: '1 1 220px', minWidth: 160 }}>
        <NexusIcon name="Search" size={13} style={{ color: 'var(--nx-text-muted)' }} />
        <input className="nx-input" style={{ flex: 1 }} placeholder="Search actions…"
          value={filters.search} onChange={e => onChange({ ...filters, search: e.target.value })} />
      </div>
      <input className="nx-input" style={{ flex: '0 1 180px' }} placeholder="Filter by actor email"
        value={filters.actor} onChange={e => onChange({ ...filters, actor: e.target.value })} />
      <input className="nx-input" style={{ flex: '0 1 170px' }} placeholder="Action (e.g. module_enabled)"
        value={filters.action} onChange={e => onChange({ ...filters, action: e.target.value })} />
      <input type="date" className="nx-input" style={{ flex: '0 1 145px' }}
        value={filters.from} onChange={e => onChange({ ...filters, from: e.target.value })}
        title="From date" aria-label="From date" />
      <input type="date" className="nx-input" style={{ flex: '0 1 145px' }}
        value={filters.to} onChange={e => onChange({ ...filters, to: e.target.value })}
        title="To date" aria-label="To date" />
      {(filters.search || filters.actor || filters.action || filters.from || filters.to) && (
        <Btn variant="ghost" icon="X" onClick={onClear}>Clear</Btn>
      )}
    </div>
  );
}

/* ── Component ───────────────────────────────────────────────────────── */
export default function AuditLog({ data }) {
  const { wsId } = useParams();
  const ws = findWorkspace(data.state, wsId);

  const [entries,  setEntries]  = useState([]);
  const [total,    setTotal]    = useState(0);
  const [page,     setPage]     = useState(1);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);
  const [filters,  setFilters]  = useState({ search: '', actor: '', action: '', from: '', to: '' });

  const fetch = useCallback(async (pg, flt) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: pg,
        limit: PAGE_SIZE,
        ...(flt.search ? { q: flt.search }       : {}),
        ...(flt.actor  ? { actor: flt.actor }    : {}),
        ...(flt.action ? { action: flt.action }  : {}),
        ...(flt.from   ? { from: flt.from }      : {}),
        ...(flt.to     ? { to: flt.to }          : {}),
      };
      const res = await workspaceApi.getAudit(wsId, params);
      setEntries(res?.data ?? []);
      setTotal(res?.pagination?.total ?? 0);
    } catch (err) {
      setError(err?.message ?? 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, [wsId]);

  useEffect(() => { fetch(1, filters); }, []);

  const applyFilters = (flt) => {
    setFilters(flt);
    setPage(1);
    fetch(1, flt);
  };

  const clearFilters = () => applyFilters({ search: '', actor: '', action: '', from: '', to: '' });

  const goPage = (p) => { setPage(p); fetch(p, filters); };

  if (!ws) return null;

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="nx-cc-page">
      <PageHead title="Audit Log" sub={'Activity log for ' + ws.name} actions={
        <Btn variant="ghost" icon="RefreshCw" onClick={() => fetch(page, filters)} disabled={loading}>
          Refresh
        </Btn>
      } />

      <FilterRow filters={filters} onChange={applyFilters} onClear={clearFilters} />

      {error && (
        <div role="alert" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', marginBottom: 12, borderRadius: 6, background: 'var(--nx-red-50,#fef2f2)', border: '1px solid var(--nx-red-200,#fecaca)', color: 'var(--nx-red-700,#b91c1c)', fontSize: 13 }}>
          <NexusIcon name="AlertCircle" size={14} /> {error}
        </div>
      )}

      {loading && entries.length === 0 && (
        <div className="nx-empty" style={{ marginTop: 40 }}>
          <NexusIcon name="Loader" size={22} />
          <span>Loading audit log…</span>
        </div>
      )}

      {!loading && entries.length === 0 && !error && (
        <div className="nx-empty">
          <NexusIcon name="ClipboardList" size={22} />
          <span>No audit entries found</span>
        </div>
      )}

      {entries.length > 0 && (
        <div className="nx-panel" style={{ overflow: 'auto', maxWidth: '100%' }}>
          <table className="nx-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, borderBottom: '1px solid var(--nx-border)', width: 160, whiteSpace: 'nowrap' }}>Timestamp</th>
                <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, borderBottom: '1px solid var(--nx-border)' }}>Action</th>
                <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, borderBottom: '1px solid var(--nx-border)' }}>Target</th>
                <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, borderBottom: '1px solid var(--nx-border)' }}>Actor</th>
                <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, borderBottom: '1px solid var(--nx-border)', width: 80 }}>Severity</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e, i) => (
                <tr key={e.id ?? i} style={{ borderBottom: '1px solid var(--nx-border-faint, #f1f5f9)' }}>
                  <td style={{ padding: '9px 12px', color: 'var(--nx-text-muted)', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                    {fmt(e.createdAt ?? e.timestamp)}
                  </td>
                  <td style={{ padding: '9px 12px', fontFamily: 'var(--nx-font-mono, monospace)', fontSize: 12 }}>
                    {e.action}
                  </td>
                  <td style={{ padding: '9px 12px', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {e.target ?? '—'}
                  </td>
                  <td style={{ padding: '9px 12px', color: 'var(--nx-text-muted)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {e.who ?? e.actor ?? '—'}
                  </td>
                  <td style={{ padding: '9px 12px' }}>
                    <Pill tone={SEV_TONE[e.sev] ?? 'gray'}>{e.sev ?? 'info'}</Pill>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, fontSize: 13 }}>
          <span style={{ color: 'var(--nx-text-muted)' }}>
            {total} entries · page {page} of {totalPages}
          </span>
          <div style={{ display: 'flex', gap: 6 }}>
            <Btn variant="ghost" icon="ChevronLeft" disabled={page <= 1 || loading} onClick={() => goPage(page - 1)}>Prev</Btn>
            <Btn variant="ghost" icon="ChevronRight" iconRight disabled={page >= totalPages || loading} onClick={() => goPage(page + 1)}>Next</Btn>
          </div>
        </div>
      )}
    </div>
  );
}
